// src/services/authService.js
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const auth = getAuth();

// Create a new student account
export const createStudentAccount = async (email, password, phoneNumber, name) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    const userData = {
      email: email,
      phoneNumber: phoneNumber,
      name: name,
      role: 'student',
      createdAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'users', user.uid), userData);

    return { success: true, user: { ...user, ...userData } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Create a new staff account
export const createStaffAccount = async (email, password, staffData) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    await setDoc(doc(db, 'users', user.uid), {
      ...staffData,
      role: 'staff',
      createdAt: serverTimestamp()
    });

    return { success: true, user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Sign in an existing user
export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      throw new Error('User data not found');
    }

    return { success: true, user: { ...user, ...userDoc.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Sign out the current user
export const logOut = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Get the current authenticated user
export const getCurrentUser = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe();
      
      if (user) {
        // Get additional user data from Firestore
        const userDoc = await getDoc(doc(db, "users", user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          resolve({
            uid: user.uid,
            email: user.email,
            phoneNumber: userData.phoneNumber,
            staffId: userData.staffId,
            name: userData.name,
            role: userData.role
          });
        } else {
          resolve(null);
        }
      } else {
        resolve(null);
      }
    });
  });
};

// Send password reset email
export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return {
      success: true,
      message: "Password reset email sent. Please check your inbox."
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
